import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import SelectPartner from 'components/partners/SelectPartner';
import ReloadButton from 'core/buttons/ReloadButton';
import { setDashboardFilters, resetDashboardFilters } from 'app/features/dashboard/dashboardSlice';
import {
  fetchTransactionSummary,
  fetchInboundPayment,
  fetchOutboundPayment,
  fetchKycCount,
} from 'app/features/dashboard/dashboardSaga';

const today = () => dayjs().format('YYYY-MM-DD');

const defaultFilters = {
  partner_id: '',
  fromDate: today(),
  toDate: today(),
  // fromDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
  //  toDate: dayjs().format('YYYY-MM-DD'),
};

export default function DashboardFilterForm() {
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  const [partnerId, setPartnerId] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const fetchAll = useCallback(
    (filters) => {
      dispatch(fetchTransactionSummary(filters));
      dispatch(fetchInboundPayment(filters));
      dispatch(fetchOutboundPayment(filters));
      dispatch(fetchKycCount(filters));
    },
    [dispatch],
  );

  const applyFilters = useCallback(
    (filters) => {
      dispatch(setDashboardFilters(filters));
      fetchAll(filters);
    },
    [dispatch, fetchAll],
  );

  useEffect(() => {
    const filterdata = JSON.parse(localStorage.getItem('dashboardFilters'));
    const filters = filterdata || defaultFilters;

    setPartnerId(filters.partner_id);
    setFromDate(dayjs(filters.fromDate));
    setToDate(dayjs(filters.toDate));

    if (isInitialMount.current) {
      applyFilters(filters);
      isInitialMount.current = false;
    } else {
      dispatch(setDashboardFilters(filters));
    }
  }, []);

  const handleFilter = () => {
    const filters = {
      partner_id: partnerId,
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD'),
    };

    localStorage.setItem('dashboardFilters', JSON.stringify(filters));
    applyFilters(filters);
  };

  const handleReload = () => {
    localStorage.removeItem('dashboardFilters');

    setPartnerId('');
    setFromDate(dayjs(defaultFilters.fromDate));
    setToDate(dayjs(defaultFilters.toDate));

    dispatch(resetDashboardFilters());
    applyFilters(defaultFilters);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={{ xs: 1.5, sm: 2, md: 2 }}
        mb={1}
      >
        <Box width={{ xs: '100%', sm: 200, md: 250, lg: 255 }}>
          <SelectPartner
            label="Select Partner"
            value={partnerId}
            onSelected={(value) => setPartnerId(value || '')}
            size="small"
          />
        </Box>

        <Box width={{ xs: '100%', sm: 200, md: 250, lg: 255 }}>
          <DatePicker
            label="From Date"
            value={fromDate}
            onChange={setFromDate}
            maxDate={toDate}
            renderInput={(p) => <TextField {...p} size="small" />}
          />
        </Box>

        <Box width={{ xs: '100%', sm: 200, md: 250, lg: 255 }}>
          <DatePicker
            label="To Date"
            value={toDate}
            onChange={setToDate}
            minDate={fromDate}
            maxDate={dayjs()}
            renderInput={(p) => <TextField {...p} size="small" />}
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleFilter}
          sx={{
            height: 32,
            width: 132,
            borderRadius: '32px',
          }}
        >
          Filter
        </Button>

        <ReloadButton onClick={handleReload} />
      </Box>
    </LocalizationProvider>
  );
}
